import React, { useRef, useState } from 'react';
import {
  Button, Box, Card, CardContent, Tabs, Tab, Divider, Link, Typography,
  TextField, CircularProgress, Chip, Paper,
} from '@material-ui/core';
import {
  CheckCircle, GetApp, ErrorOutline, Search, CloudDownload,
} from '@material-ui/icons';
import MUIDataTable from 'mui-datatables';

// ---------------------------------------------------------------------------
// OpenSincera API Explorer
// ---------------------------------------------------------------------------
const API_TOKEN = "2b0019cc43f5e0653066";
const API_BASE  = "http://mobileappscrapper.herokuapp.com/js/https://open.sincera.io/api";

function prettyKey(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}
function renderObj(obj, depth = 0) {
  if (obj === null || obj === undefined)
    return <Typography variant="caption" color="textSecondary" style={{ fontStyle: 'italic' }}>None</Typography>;
  if (typeof obj !== "object")
    return <Typography component="span" variant="body2">{String(obj)}</Typography>;
  if (Array.isArray(obj)) {
    if (!obj.length)
      return <Typography variant="caption" color="textSecondary">[]</Typography>;
    return (
      <Box style={{ marginLeft: 12, borderLeft: '2px solid #eee', paddingLeft: 10 }}>
        {obj.map((item, i) => <Box key={i} mb={0.5}>{renderObj(item, depth + 1)}</Box>)}
      </Box>
    );
  }
  return (
    <Box component="dl" style={{ margin: 0 }}>
      {Object.entries(obj).map(([k, v]) => (
        <Box key={k} mb={1.5}>
          <Typography component="dt" variant="caption" style={{ fontWeight: 700, color: '#fe5000', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {prettyKey(k)}
          </Typography>
          <Typography component="dd" style={{ marginLeft: 8 }}>{renderObj(v, depth + 1)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function OpenSinceraExplorer() {
  const [mode, setMode]     = useState("ecosystem");
  const [input, setInput]   = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData]     = useState(null);
  const [error, setError]   = useState("");

  const fetchData = async (e) => {
    e?.preventDefault();
    setLoading(true); setData(null); setError("");
    const urlMap = {
      ecosystem:       `${API_BASE}/ecosystem`,
      publisherId:     `${API_BASE}/publishers?id=${input.trim()}`,
      publisherDomain: `${API_BASE}/publishers?domain=${input.trim()}`,
    };
    try {
      const resp = await fetch(urlMap[mode], { headers: { Authorization: `Bearer ${API_TOKEN}` } });
      let json = null;
      try { json = await resp.json(); } catch {}
      if (resp.status === 404)   setError("No results found for this query.");
      else if (!resp.ok)         setError(json?.error ? String(json.error) : "Something went wrong. Try again later.");
      else if (json?.error)      setError(String(json.error));
      else                       setData(json);
    } catch {
      setError("Network error — check your connection.");
    } finally {
      setLoading(false);
    }
  };

  const needsInput = mode === "publisherId" || mode === "publisherDomain";

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={2} style={{ gap: 10 }}>
        <Search style={{ color: '#fe5000' }} />
        <Typography variant="h6" style={{ fontWeight: 700 }}>OpenSincera API Explorer</Typography>
      </Box>
      <Tabs
        value={mode}
        onChange={(_, v) => { setMode(v); setInput(""); setData(null); setError(""); }}
        indicatorColor="secondary" textColor="secondary"
        style={{ marginBottom: 16, borderBottom: '1px solid #e0e0e0' }}
      >
        <Tab value="ecosystem"       label="Ecosystem Data" />
        <Tab value="publisherId"     label="Publisher by ID" />
        <Tab value="publisherDomain" label="Publisher by Domain" />
      </Tabs>

      <Box component="form" onSubmit={fetchData} mb={2}>
        {needsInput && (
          <TextField
            label={mode === "publisherId" ? "Publisher ID" : "Publisher Domain"}
            placeholder={mode === "publisherId" ? "e.g. 1" : "e.g. businessinsider.com"}
            variant="outlined" value={input} onChange={e => setInput(e.target.value)}
            fullWidth required size="small" style={{ marginBottom: 12 }}
          />
        )}
        <Button
          type={needsInput ? "submit" : "button"}
          variant="contained" color="primary"
          disabled={loading} onClick={needsInput ? undefined : fetchData}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Search />}
          style={{ borderRadius: 8, fontWeight: 700 }}
        >
          {loading ? "Searching…" : needsInput ? "Search" : "Get Ecosystem Data"}
        </Button>
      </Box>

      {error && (
        <Box display="flex" alignItems="center" style={{ gap: 8, color: '#e74c3c', marginBottom: 12, padding: '10px 14px', background: '#fdf2f2', borderRadius: 8, border: '1px solid #f5c6cb' }}>
          <ErrorOutline fontSize="small" />
          <Typography variant="body2" style={{ color: '#e74c3c' }}>{error}</Typography>
        </Box>
      )}

      {data && (
        <Card variant="outlined" style={{ background: '#fafbff', borderRadius: 10 }}>
          <CardContent>{renderObj(data)}</CardContent>
        </Card>
      )}

      <Divider style={{ margin: '20px 0' }} />
      <Typography variant="caption" color="textSecondary" style={{ display: 'block', textAlign: 'center' }}>
        Data powered by{" "}
        <Link href="https://open.sincera.io" target="_blank" rel="noopener" style={{ color: '#fe5000' }}>
          OpenSincera
        </Link>
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Stat card helper
// ---------------------------------------------------------------------------
function StatCard({ label, value, color }) {
  return (
    <Paper elevation={0} style={{
      padding: '10px 18px', borderRadius: 10, textAlign: 'center',
      background: `${color}18`, border: `1px solid ${color}44`, minWidth: 90,
    }}>
      <Typography style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value ?? '—'}</Typography>
      <Typography variant="caption" style={{ color: '#666', fontWeight: 600 }}>{label}</Typography>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main JsonExporter component
// ---------------------------------------------------------------------------
export default function JsonExporter() {
  const jsonRef    = useRef();
  const validateRef = useRef();

  const [tab, setTab]             = useState(0);
  const [checkError, setCheckError] = useState("");
  const [validateErrors, setValidateErrors] = useState([]);
  const [validateOk, setValidateOk]     = useState(false);
  const [analysed, setAnalysed]         = useState(false);

  const [typeData,      setTypeData]      = useState([]);
  const [idData,        setIdData]        = useState([]);
  const [companyData,   setCompanyData]   = useState([]);
  const [domainData,    setDomainData]    = useState([]);
  const [totalSellers,  setTotalSellers]  = useState(0);
  const [dupIdCount,    setDupIdCount]    = useState(0);
  const [dupNameCount,  setDupNameCount]  = useState(0);

  // ── Analyse ──
  function handleCheck() {
    setCheckError("");
    const raw = jsonRef.current?.value || "";
    let parsed;
    try { parsed = JSON.parse(raw); } catch {
      setCheckError("Invalid JSON — please paste a valid sellers.json file.");
      return;
    }
    if (!parsed.sellers || !Array.isArray(parsed.sellers)) {
      setCheckError("JSON is valid but doesn't contain a 'sellers' array.");
      return;
    }
    const sellers = parsed.sellers;
    setTotalSellers(sellers.length);

    const names = {}, doms = {}, types = {}, ids = {};
    for (const s of sellers) {
      const n = s.name || ""; names[n] = (names[n] || 0) + 1;
      const d = s.domain || ""; doms[d] = (doms[d] || 0) + 1;
      const t = (s.seller_type || "").toUpperCase(); types[t] = (types[t] || 0) + 1;
      const i = s.seller_id || ""; ids[i] = (ids[i] || 0) + 1;
    }

    setTypeData(Object.entries(types).map(([t, c]) => [t, c]));
    setIdData(Object.entries(ids).map(([i, c]) => [i, c]));
    setCompanyData(Object.entries(names).map(([n, c]) => [n, c]));
    setDomainData(Object.entries(doms).map(([d, c]) => [d, c]));

    const dupIds   = Object.values(ids).filter(c => c > 1).length;
    const dupNames = Object.values(names).filter(c => c > 1 && Object.keys(names).find(k => names[k] === c && k !== "")).length;
    setDupIdCount(dupIds);
    setDupNameCount(dupNames);
    setAnalysed(true);
  }

  function handleExport() {
    setCheckError("");
    const raw = (jsonRef.current?.value || "").replace(/&#39;/g, "").replace(/#/g, "");
    let parsed;
    try { parsed = JSON.parse(raw); } catch {
      setCheckError("Invalid JSON — cannot export.");
      return;
    }
    const rows = parsed.sellers.map(s => ({
      seller_id:      s.seller_id,
      is_confidential: s.is_confidential,
      seller_type:    s.seller_type,
      is_passthrough: s.is_passthrough,
      name:           s.name,
      domain:         s.domain,
      comment:        s.comment,
      ext:            s.ext,
    }));
    const header = Object.keys(rows[0]).join(",");
    const lines  = rows.map(r => Object.values(r).map(v => typeof v === "string" ? JSON.stringify(v) : v).join(","));
    const csv    = [header, ...lines].join("\n");
    const a      = document.createElement("a");
    a.href       = "data:attachment/text," + encodeURI(csv);
    a.download   = "sellers.csv";
    a.click();
  }

  // ── Validate ──
  function handleValidate() {
    setValidateErrors([]); setValidateOk(false);
    const raw = validateRef.current?.value || "";
    const removed    = raw.split('sellers": [').pop();
    const cleaned    = removed.replaceAll("\n      ", "").replaceAll("    ", "").replaceAll("\n", "");
    const lines      = cleaned.split("},");
    const errors     = [];
    const JsonLines  = [];

    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      if (line.endsWith("]")) line = line.slice(0, -1);
      if (!line.endsWith("}")) line += "}";
      if (line.endsWith('"1.0"}')) line = line.split("]")[0];
      if (line.endsWith("}  ]}")) line = line.split("]")[0];
      try { JsonLines[i] = JSON.parse(line); }
      catch (e) { errors.push(`Line ${i + 1}: ${e.message}`); }
    }

    for (let i = 0; i < JsonLines.length; i++) {
      const s = JsonLines[i];
      if (!s) continue;
      if (Number(s.is_confidential) === 0 && !s.name)
        errors.push(`Seller ${s.seller_id || i}: name is missing`);
      const type = (s.seller_type || "").toLowerCase();
      if (!["publisher", "intermediary", "both"].includes(type))
        errors.push(`Seller ${s.seller_id || i}: invalid seller_type "${s.seller_type}"`);
      if (Number(s.is_confidential) === 0 && !s.domain)
        errors.push(`Seller ${s.seller_id || i}: not confidential but domain is missing`);
      if (!s.seller_id)
        errors.push(`Seller ${s.name || i}: seller_id is missing`);
    }

    if (errors.length === 0) setValidateOk(true);
    else setValidateErrors(errors);
  }

  const tableOpts = (filename) => ({
    filterType: "checkbox", selectableRows: "none", responsive: "standard",
    rowsPerPage: 10,
    downloadOptions: { filename: `${filename}_${new Date().toISOString().slice(0, 10)}.csv` },
    textLabels: { body: { noMatch: "Analyse sellers.json first" } },
    sortOrder: { name: "Instances", direction: "desc" },
  });

  return (
    <Box>
      <Tabs
        value={tab} onChange={(_, v) => setTab(v)}
        indicatorColor="secondary" textColor="secondary"
        style={{ borderBottom: '1px solid #e0e0e0', marginBottom: 16 }}
      >
        <Tab label="Analyse & Export" />
        <Tab label="Validate" />
        <Tab label="OpenSincera API" />
      </Tabs>

      {/* ── TAB 0: Analyse & Export ── */}
      {tab === 0 && (
        <Box>
          <Card style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <CardContent>
              <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 8 }}>Paste Sellers.json</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                Paste the full contents of a <strong>sellers.json</strong> file below, then analyse or export it.
              </Typography>
              <TextField
                variant="outlined" multiline rows={10} fullWidth
                inputRef={jsonRef}
                placeholder={`{\n  "version": "1.0",\n  "sellers": [\n    { "seller_id": "...", "seller_type": "PUBLISHER", ... }\n  ]\n}`}
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                style={{ marginBottom: 12 }}
              />
              {checkError && (
                <Box display="flex" alignItems="center" style={{ gap: 8, color: '#e74c3c', marginBottom: 12, padding: '10px 14px', background: '#fdf2f2', borderRadius: 8, border: '1px solid #f5c6cb' }}>
                  <ErrorOutline fontSize="small" />
                  <Typography variant="body2" style={{ color: '#e74c3c' }}>{checkError}</Typography>
                </Box>
              )}
              <Box display="flex" style={{ gap: 10 }}>
                <Button
                  variant="contained" color="primary"
                  onClick={handleCheck}
                  startIcon={<CheckCircle />}
                  style={{ borderRadius: 8, fontWeight: 700 }}
                >
                  Analyse
                </Button>
                <Button
                  variant="outlined" color="secondary"
                  onClick={handleExport}
                  startIcon={<CloudDownload />}
                  style={{ borderRadius: 8, fontWeight: 700 }}
                >
                  Export to CSV
                </Button>
              </Box>
            </CardContent>
          </Card>

          {analysed && (
            <>
              {/* Stats row */}
              <Box display="flex" flexWrap="wrap" style={{ gap: 10, marginBottom: 20 }}>
                <StatCard label="Total Sellers" value={totalSellers} color="#636e72" />
                <StatCard label="Duplicate IDs"    value={dupIdCount}    color={dupIdCount > 0 ? '#e74c3c' : '#27ae60'} />
                <StatCard label="Duplicate Names"  value={dupNameCount}  color={dupNameCount > 0 ? '#f39c12' : '#27ae60'} />
                <StatCard label="Publisher"    value={typeData.find(r => r[0] === 'PUBLISHER')?.[1]    ?? 0} color="#0984e3" />
                <StatCard label="Intermediary" value={typeData.find(r => r[0] === 'INTERMEDIARY')?.[1] ?? 0} color="#6c5ce7" />
                <StatCard label="Both"         value={typeData.find(r => r[0] === 'BOTH')?.[1]         ?? 0} color="#00b894" />
              </Box>

              <MUIDataTable title="Seller Types"    data={typeData}    columns={["Type",    { name: "Instances", options: { sortDirection: 'desc' } }]} options={tableOpts("SellerTypes")} />
              <Box mt={2} />
              <MUIDataTable title="Seller IDs"      data={idData}      columns={["ID",      { name: "Instances", options: { sortDirection: 'desc' } }]} options={tableOpts("SellerIds")} />
              <Box mt={2} />
              <MUIDataTable title="Company Count"   data={companyData} columns={["Company", { name: "Instances", options: { sortDirection: 'desc' } }]} options={tableOpts("CompanyCount")} />
              <Box mt={2} />
              <MUIDataTable title="Domain Count"    data={domainData}  columns={["Domain",  { name: "Instances", options: { sortDirection: 'desc' } }]} options={tableOpts("DomainCount")} />
            </>
          )}
        </Box>
      )}

      {/* ── TAB 1: Validate ── */}
      {tab === 1 && (
        <Box>
          <Card style={{ marginBottom: 16, borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <CardContent>
              <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 8 }}>Validate Sellers.json</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                Checks each seller entry for missing required fields, invalid seller types, and domain/confidentiality mismatches.
              </Typography>
              <TextField
                variant="outlined" multiline rows={10} fullWidth
                inputRef={validateRef}
                placeholder="Paste sellers.json content here…"
                inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                style={{ marginBottom: 12 }}
              />
              <Button
                variant="contained" color="primary"
                onClick={handleValidate}
                startIcon={<CheckCircle />}
                style={{ borderRadius: 8, fontWeight: 700 }}
              >
                Validate
              </Button>
            </CardContent>
          </Card>

          {validateOk && (
            <Box display="flex" alignItems="center" style={{ gap: 10, padding: '14px 20px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10 }}>
              <CheckCircle style={{ color: '#27ae60' }} />
              <Typography style={{ color: '#27ae60', fontWeight: 700 }}>No validation errors found — sellers.json looks good!</Typography>
            </Box>
          )}

          {validateErrors.length > 0 && (
            <Card style={{ borderRadius: 12, border: '1px solid #f5c6cb', boxShadow: 'none' }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={2} style={{ gap: 8 }}>
                  <ErrorOutline style={{ color: '#e74c3c' }} />
                  <Typography variant="h6" style={{ fontWeight: 700, color: '#e74c3c' }}>
                    {validateErrors.length} validation error{validateErrors.length > 1 ? 's' : ''} found
                  </Typography>
                </Box>
                <Box style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {validateErrors.map((err, i) => (
                    <Box key={i} style={{ padding: '8px 12px', background: '#fff5f5', borderRadius: 6, border: '1px solid #fed7d7' }}>
                      <Typography variant="body2" style={{ fontFamily: 'monospace', color: '#c0392b', fontSize: 12 }}>
                        {err}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* ── TAB 2: OpenSincera API ── */}
      {tab === 2 && (
        <Card style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
          <CardContent>
            <OpenSinceraExplorer />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
